import { createContext, useMemo, useState } from 'react';

const onEventList: { type: string; callback: Function }[] = [];

interface Storage {
  setItem: Function;
  getItem: Function;
}

/**
 * A hook to allow getting and setting items to storage, hook comes
 * with context and also event listener like functionality
 *
 * @param type either local or session
 *
 * @example
 * const storage = useLocalStorage('local');
 * <StorageContext.Provider value={storage}>...</StorageContext.Provider>
 */
export const useLocalStorage = (type: 'local' | 'session'): {
    set: (key: string, data: any) => void;
    get: (key: string) => any;
    on: (event: string, callback: Function) => void;
    off: (event: string, func: Function) => void;
} => {

  const [storageType] = useState<Storage>((window as any)[`${type}Storage`]);

  // Prevent rerun on parent redraw
  return useMemo(() => {
    /**
     * Set the data, generally you will need to get the data modify it then set it
     * When we use on it's added to onEventList then, we filter the array based on items that match by type then run all the callbacks.
     *
     * @param key key to be used in the storage table
     * @param data data to be passed in to the storage table as the value
     *
     * @example storage.set('list', ['item1','item2'])
     *
     * @event `set` the key is passed through
     */
    const set = (key: string, data: any) => {
      const type = typeof data;
      if (type === 'object') {
        data = JSON.stringify(data);
      }
      storageType.setItem(key, data);
      storageType.setItem(`$$${key}_data`, type);
      onEventList
        .filter((obj) => obj.type === 'set')
        .forEach((obj) => obj.callback(key));
    };

    /**
     * Get the data.
     *
     * @param key key to be fetched from the storage table
     *
     * @example const tableName = storage.get('table_name');
     *
     * @event `get` the key is passed through
     *
     * @returns contents of selected key
     */
    const get = (key: string) => {
      const type = storageType.getItem(`$$${key}_data`);
      const data = storageType.getItem(key);

      onEventList
        .filter((obj) => obj.type === 'get')
        .forEach((obj) => obj.callback(key));

      switch (type) {
        case 'object':
          return JSON.parse(data);
        case 'number':
          return parseFloat(data);
        case 'boolean':
          return data === 'true';
        case 'undefined':
          return undefined;
        default:
          return data;
      }
    };

    /**
     * Add event listener for when this component is used.
     *
     * @param event name of event triggered by function
     * @param func a callback function to be called when event matches
     *
     * @example storage.on('set', (key) => {
     *   const data = storage.get(key);
     *   console.log(data)
     * })
     */
    const on = (event: string, callback: Function) => {
      onEventList.push({ type: event, callback });
    };

    /**
     * If you exactly match an `on` event you can remove it
     *
     * @param event matching event name
     * @param func matching function
     */
    const off = (event: string, func: Function) => {
      const remove = onEventList.indexOf(
        onEventList.filter((e) => e.type === event && e.callback === func)[0],
      );
      if (remove >= 0) {
        onEventList.splice(remove, 1);
      }
    };

    return { set, get, on, off };
  }, [storageType]);
}

export const StorageContext = createContext(null);
